import { Button } from '@/components/ui/Button';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, Lock, RotateCcw, Send } from 'lucide-react';
import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { SirhPageHeader } from '../components/SirhPageHeader';
import { sirhService } from '../services/sirh.service';
import type { VariablePeriod } from '../types';

const STATE_LABELS: Record<VariablePeriod['state'], string> = {
  ouverte: 'Ouverte à la saisie',
  gelee: 'Gelée',
  transmise: 'Transmise',
  rouverte: 'Rouverte',
};

function currentPeriod(): string {
  const now = new Date();
  return `${now.getUTCFullYear()}-${String(now.getUTCMonth() + 1).padStart(2, '0')}`;
}

/**
 * Écran cabinet — « Campagne de variables ».
 *
 * Le contrôle de transmissibilité est montré AVANT le gel : le gestionnaire voit
 * ce qui bloque au moment où il peut encore le corriger, pas au moment de
 * transmettre. Une période transmise n'affiche plus de bouton de saisie — l'écran
 * dit la même chose que la base, parce qu'il lit son état au lieu de le deviner.
 */
export function VariableCampaignPage() {
  const { folderUuid } = useParams();
  const queryClient = useQueryClient();
  const [period, setPeriod] = useState(currentPeriod());
  const [reopenReason, setReopenReason] = useState('');

  const { data: variablePeriod } = useQuery({
    queryKey: ['sirh', 'variable-period', folderUuid, period],
    queryFn: () => sirhService.openVariablePeriod(folderUuid as string, period),
    enabled: Boolean(folderUuid),
  });

  const { data: check } = useQuery({
    queryKey: ['sirh', 'variable-check', variablePeriod?.uuid],
    queryFn: () => sirhService.checkVariables(variablePeriod?.uuid as string),
    enabled: Boolean(variablePeriod?.uuid),
  });

  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: ['sirh', 'variable-period'] });
    void queryClient.invalidateQueries({ queryKey: ['sirh', 'variable-check'] });
  };

  const freeze = useMutation({
    mutationFn: () =>
      sirhService.freezeVariablePeriod(
        variablePeriod?.uuid as string,
        variablePeriod?.version as number,
      ),
    onSuccess: invalidate,
  });

  const transmit = useMutation({
    mutationFn: () =>
      sirhService.transmitVariablePeriod(
        variablePeriod?.uuid as string,
        variablePeriod?.version as number,
      ),
    onSuccess: invalidate,
  });

  const reopen = useMutation({
    mutationFn: () =>
      sirhService.reopenVariablePeriod(
        variablePeriod?.uuid as string,
        variablePeriod?.version as number,
        reopenReason,
      ),
    onSuccess: () => {
      setReopenReason('');
      invalidate();
    },
  });

  const state = variablePeriod?.state;
  const rejections = check?.rejections ?? [];

  return (
    <div className="space-y-6">
      <SirhPageHeader
        title="Campagne de variables"
        subtitle="Le SIRH prépare, contrôle, fait approuver, transmet et conserve le reçu. Il ne calcule aucun bulletin."
        actions={
          <input
            type="month"
            value={period}
            onChange={event => setPeriod(event.target.value)}
            className="h-10 rounded-xl border border-neutral-300 px-3 text-sm"
            aria-label="Période de paie"
          />
        }
      />

      <section className="rounded-xl border border-neutral-200 bg-white">
        <div className="flex flex-wrap items-center justify-between gap-4 px-6 py-4">
          <div>
            <p className="text-sm font-medium text-neutral-900">
              {state ? STATE_LABELS[state] : 'Période inconnue'}
            </p>
            <p className="text-xs text-neutral-500">
              {rejections.length === 0
                ? 'Aucun point bloquant : la période peut être gelée puis transmise.'
                : `${rejections.length} point${rejections.length > 1 ? 's' : ''} bloquant${rejections.length > 1 ? 's' : ''} avant transmission.`}
            </p>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            {state === 'ouverte' || state === 'rouverte' ? (
              <Button
                variant="outline"
                className="gap-2"
                disabled={freeze.isPending}
                onClick={() => freeze.mutate()}
              >
                <Lock className="h-4 w-4" />
                Geler la période
              </Button>
            ) : null}

            {state === 'gelee' ? (
              <Button
                variant="brand"
                className="gap-2"
                disabled={rejections.length > 0 || transmit.isPending}
                onClick={() => transmit.mutate()}
              >
                <Send className="h-4 w-4" />
                Transmettre
              </Button>
            ) : null}
          </div>
        </div>

        {rejections.length > 0 && (
          <ul className="divide-y divide-neutral-100 border-t border-neutral-100">
            {rejections.map(rejection => (
              <li
                key={`${rejection.code}-${rejection.variableUuid ?? 'periode'}`}
                className="flex items-start gap-3 px-6 py-3 text-sm"
              >
                <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-warning-500" />
                <span className="text-neutral-700">{rejection.message}</span>
              </li>
            ))}
          </ul>
        )}
      </section>

      {(state === 'transmise' || state === 'gelee') && (
        <section className="rounded-xl border border-neutral-200 bg-white px-6 py-4">
          <p className="text-sm font-medium text-neutral-900">Rouvrir la période</p>
          <p className="mt-1 text-xs text-neutral-500">
            Une période transmise ne se modifie pas. La rouvrir exige un motif écrit, conservé
            avec le nom de la personne qui l’a demandé.
          </p>
          <div className="mt-4 flex flex-wrap items-center gap-4">
            <input
              value={reopenReason}
              onChange={event => setReopenReason(event.target.value)}
              placeholder="Motif de la réouverture"
              className="h-10 flex-1 rounded-xl border border-neutral-300 px-3 text-sm placeholder:text-neutral-400/50"
            />
            <Button
              variant="outline"
              className="gap-2"
              disabled={reopenReason.trim().length < 10 || reopen.isPending}
              onClick={() => reopen.mutate()}
            >
              <RotateCcw className="h-4 w-4" />
              Rouvrir
            </Button>
          </div>
        </section>
      )}
    </div>
  );
}
